/**
 * Promociones: códigos de descuento de Paddle. Se crean aquí (porcentaje, meses,
 * usos y vencimiento) y se archivan; Paddle los aplica en el checkout.
 */
import { useState } from 'react'
import { errorText, useApp, useToast } from '../../components/AppContext'
import { ErrorBox, Skeleton } from '../../components/ui'
import { finDelDiaChile, hoyEnChile } from '../../lib/adminFormat'
import { formatDate } from '../../lib/format'
import { useAsync, useDocumentTitle } from '../../lib/hooks'
import { Seccion } from './common'

export function Promociones() {
  useDocumentTitle('Promociones · Administración')
  const { api } = useApp()
  const toast = useToast()
  const lista = useAsync(() => api.adminDescuentos(), [api])
  const [codigo, setCodigo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  // Como texto: el campo se puede vaciar para reescribirlo; se valida antes de crear
  const [porcentajeTxt, setPorcentaje] = useState('20')
  const [meses, setMeses] = useState<string>('3')
  const [maxUsos, setMaxUsos] = useState('')
  const [expira, setExpira] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const codigoValido = /^[a-zA-Z0-9]{3,32}$/.test(codigo)
  const porcentaje = Number(porcentajeTxt)
  const porcentajeOk = Number.isInteger(porcentaje) && porcentaje >= 1 && porcentaje <= 100
  const hoy = hoyEnChile()
  // Las fechas `YYYY-MM-DD` se comparan bien como texto
  const expiraOk = expira === '' || expira >= hoy
  const usosOk = maxUsos === '' || (Number.isInteger(Number(maxUsos)) && Number(maxUsos) >= 1)
  const formularioOk = codigoValido && porcentajeOk && expiraOk && usosOk

  async function crear() {
    setOcupado(true)
    setError(null)
    try {
      await api.adminCrearDescuento({
        codigo,
        descripcion: descripcion.trim() || `${porcentaje} % ${codigo}`,
        porcentaje,
        meses: meses === 'siempre' ? null : Number(meses),
        maxUsos: maxUsos ? Number(maxUsos) : null,
        // Vence al final del día elegido, en hora de Chile
        expiraEn: expira ? finDelDiaChile(expira).toISOString() : null
      })
      toast(`Código ${codigo.toUpperCase()} creado`)
      setCodigo('')
      setDescripcion('')
      await lista.reload()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setOcupado(false)
    }
  }

  async function archivar(id: string, cod: string | null) {
    if (!confirm(`¿Archivar ${cod ?? 'este descuento'}? Ya no se podrá usar en el checkout.`))
      return
    try {
      await api.adminArchivarDescuento(id)
      toast('Descuento archivado')
      await lista.reload()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  return (
    <div className="admin-grid">
      <div className="span-4">
        <Seccion titulo="Nuevo código">
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault()
              if (formularioOk) void crear()
            }}
          >
            <label className="field">
              <span>Código (letras y números)</span>
              <input
                value={codigo}
                maxLength={32}
                onChange={(e) => setCodigo(e.target.value.replace(/\s/g, ''))}
                placeholder="LANZAMIENTO20"
                aria-invalid={codigo !== '' && !codigoValido}
              />
            </label>
            <label className="field">
              <span>Descuento</span>
              <div className="row" style={{ gap: 6 }}>
                <input
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  value={porcentajeTxt}
                  onChange={(e) => setPorcentaje(e.target.value)}
                  aria-invalid={!porcentajeOk}
                  style={{ width: 90 }}
                  aria-label="Porcentaje de descuento"
                />
                <span className="faint">%</span>
              </div>
            </label>
            <label className="field">
              <span>Dura</span>
              <select value={meses} onChange={(e) => setMeses(e.target.value)}>
                <option value="1">Solo el primer mes</option>
                <option value="3">3 meses</option>
                <option value="6">6 meses</option>
                <option value="12">12 meses</option>
                <option value="siempre">Para siempre</option>
              </select>
            </label>
            <label className="field">
              <span>Usos máximos (vacío = sin límite)</span>
              <input
                type="number"
                min={1}
                step={1}
                value={maxUsos}
                aria-invalid={!usosOk}
                onChange={(e) => setMaxUsos(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Vence (opcional)</span>
              <input
                type="date"
                min={hoy}
                value={expira}
                onChange={(e) => setExpira(e.target.value)}
                aria-invalid={!expiraOk}
              />
              {!expiraOk && <small className="faint">Elige hoy o una fecha futura.</small>}
            </label>
            <label className="field">
              <span>Nota interna (no la ven los clientes)</span>
              <input
                value={descripcion}
                maxLength={200}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Ej.: campaña de lanzamiento en LinkedIn"
              />
            </label>
            {error && (
              <div className="notice error" role="alert">
                {error}
              </div>
            )}
            <div>
              <button className="btn primary" disabled={!formularioOk || ocupado}>
                {ocupado ? 'Creando…' : 'Crear código'}
              </button>
            </div>
          </form>
        </Seccion>
      </div>
      <div className="span-8">
        <Seccion titulo="Códigos">
          {lista.error && <ErrorBox error={lista.error} onRetry={() => void lista.reload()} />}
          {!lista.data && !lista.error && <Skeleton h={120} />}
          {lista.data && lista.data.items.length === 0 && (
            <p className="faint" style={{ margin: 0 }}>
              Todavía no hay códigos de descuento.
            </p>
          )}
          {lista.data && lista.data.items.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="simple">
                <caption className="sr">Códigos de descuento</caption>
                <thead>
                  <tr>
                    <th scope="col">Código</th>
                    <th scope="col" className="num">
                      Descuento
                    </th>
                    <th scope="col">Dura</th>
                    <th scope="col" className="num">
                      Usos
                    </th>
                    <th scope="col">Vence</th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {lista.data.items.map((x) => (
                    <tr key={x.id} className={x.estado === 'archived' ? 'faint' : undefined}>
                      <td>
                        <span className="mono" style={{ fontWeight: 550 }}>
                          {x.codigo ?? '—'}
                        </span>
                        <div className="faint">{x.descripcion}</div>
                      </td>
                      <td className="num">{x.porcentaje} %</td>
                      <td>
                        {x.meses === null
                          ? 'Siempre'
                          : x.meses === 1
                            ? '1 mes'
                            : `${x.meses} meses`}
                      </td>
                      <td className="num">
                        {x.usos}
                        {x.maxUsos ? ` / ${x.maxUsos}` : ''}
                      </td>
                      <td>{x.expiraEn ? formatDate(x.expiraEn) : '—'}</td>
                      <td>
                        {x.estado === 'active' ? (
                          <button
                            className="btn sm ghost"
                            onClick={() => void archivar(x.id, x.codigo)}
                          >
                            Archivar
                          </button>
                        ) : (
                          <span className="faint">Archivado</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="faint" style={{ margin: 0, fontSize: 12 }}>
            Los códigos viven en Paddle y se aplican en el checkout de Personal y Equipo. El MRR del
            resumen está a precio de lista, sin descontarlos.
          </p>
        </Seccion>
      </div>
    </div>
  )
}
